// Shared helpers for parsing and averaging the "Course:Mark•Course:Mark" report-card
// cell format used throughout DataDisplay/DataTable.

// Parses a "Course:Mark\nCourse:Mark" report-card cell into a flat [course, mark, ...] array.
export function parseMarksString(raw) {
  if (!raw) return [];

  return raw
    .replaceAll("•", "")
    .replaceAll(" ", "")
    .replaceAll("SocialStudies", "Social Studies")
    .split(/:|\n/);
}

// Averages term1/term2 marks for every course mentioned in EITHER term (a course graded
// in only one term — new mid-year, dropped, or simply not entered yet in the other term —
// still shows up, with just that term's mark).
export function averageTermMarks(term1, term2) {
  const courseNames = [];
  const seen = new Set();

  const collectCourseNames = (courses) => {
    for (let i = 0; i < courses.length; i += 2) {
      const course = courses[i];
      if (course && !seen.has(course)) {
        seen.add(course);
        courseNames.push(course);
      }
    }
  };
  collectCourseNames(term1);
  collectCourseNames(term2);

  const result = [];

  courseNames.forEach((course) => {
    const i1 = term1.indexOf(course);
    const i2 = term2.indexOf(course);
    const mark1 = i1 !== -1 ? term1[i1 + 1] : undefined;
    const mark2 = i2 !== -1 ? term2[i2 + 1] : undefined;

    result.push(course);
    if (mark1 !== undefined && mark2 !== undefined) {
      result.push(parseFloat((Number(mark1) + Number(mark2)) / 2).toFixed(2));
    } else {
      result.push(mark1 !== undefined ? mark1 : mark2);
    }
  });

  return result;
}

// Averages a list of marks, ignoring missing/non-numeric/zero (i.e. "no mark entered") values.
export function average(marks) {
  let sum = 0;
  let count = 0;

  for (let i = 0; i < marks.length; i++) {
    if (typeof marks[i] === "number" && !isNaN(marks[i]) && marks[i] !== 0) {
      sum += marks[i];
      count++;
    }
  }

  return count === 0 ? 0 : (sum / count).toFixed(2);
}

// Converts a raw cell value to a number, or null when there's no usable mark
// (missing/empty/non-numeric) so charts render a gap instead of a false zero.
export function numOrNull(value) {
  return value !== undefined && value !== "" && !isNaN(value)
    ? Number(value)
    : null;
}

// Like average(), but ignores bad values individually (instead of one bad entry poisoning
// the whole sum to NaN) and returns a plain number/null instead of a formatted string/0 —
// what chart data (Math.min/max, series values) needs.
export function averageOrNull(marks) {
  const valid = marks.filter(
    (mark) => typeof mark === "number" && !isNaN(mark) && mark !== 0,
  );

  if (valid.length === 0) return null;

  return valid.reduce((sum, mark) => sum + mark, 0) / valid.length;
}
